// Implements: F-VER-008
/**
 * The note of one version in the timeline (mockup v3 :2605, F-VER-008).
 *
 * Read-only for a viewer. For whoever may write, "Añadir nota" (or the pencil of an existing note)
 * opens an inline field capped at 140 characters (the server's bound, P4-B4): Enter or "Guardar"
 * sends the trimmed text once, a blank note is sent as `null` (no note), Escape cancels without a
 * request and keeps the popover open. A refusal keeps the field open with the server's sentence.
 * A saved note refreshes the timeline's notes and the tree.
 *
 * The answer of a save is shown at once, but only while the `note` prop still carries the note that
 * save replaced: as soon as the refetched prop moves (the saved value itself, or a newer one written
 * from another tab or by another person), the server's value wins again (audit 2026-10-19). The
 * prop alone cannot tell "not refetched yet" from "refetched, and someone undid the edit": both carry
 * the replaced note. So the override also ends once the notes refetch that follows the save has
 * answered with data (verifier follow-up 2026-10-19); a refetch that fails keeps the saved answer,
 * the newest value known. The override is bound to its version id, so it never leaks to another
 * version (checklist 14).
 */

import { useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import type { ReactElement } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import { versionTreeKey } from "../../../shared/api/queryKeys";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Input } from "../../../shared/ui/atoms/Input";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { annotateVersion } from "../api/versionsApi";
import { versionNotesKey } from "../hooks/useVersionNotes";
import styles from "./VersionNoteContainer.module.css";

/** The longest note the server keeps (P4-B4). */
export const NOTE_MAX_LENGTH = 140;

export interface VersionNoteContainerProps {
  readonly mockupId: string;
  readonly versionId: string;
  /** The version number, for the accessible names ("Nota de la v2"). */
  readonly sequenceNumber?: number | undefined;
  readonly note: string | null;
  /** Whether the reader may edit the note. */
  readonly editable: boolean;
}

/** The answer of the last save, and the note it replaced, for one version. */
interface SavedNote {
  readonly versionId: string;
  readonly replaced: string | null;
  readonly value: string | null;
}

export function VersionNoteContainer({
  mockupId,
  versionId,
  sequenceNumber = 0,
  note,
  editable,
}: VersionNoteContainerProps): ReactElement | null {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fieldId = useId();
  const [saved, setSaved] = useState<SavedNote | undefined>(undefined);
  const [draft, setDraft] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<string | undefined>(undefined);
  const sending = useRef(false);
  const overridden = saved?.versionId === versionId && saved.replaced === note;
  if (saved !== undefined && !overridden) setSaved(undefined);
  const shown = overridden ? saved.value : note;

  const cancel = (): void => {
    setDraft(null);
    setRefusal(undefined);
  };

  const save = async (): Promise<void> => {
    if (sending.current || draft === null) return;
    sending.current = true;
    setPending(true);
    setRefusal(undefined);
    const trimmed = draft.trim();
    try {
      const answer = await annotateVersion(mockupId, versionId, trimmed === "" ? null : trimmed);
      const entry: SavedNote = { versionId, replaced: note, value: answer.note };
      setSaved(entry);
      setDraft(null);
      const notesKey = versionNotesKey(mockupId);
      const readsBefore = queryClient.getQueryState(notesKey)?.dataUpdateCount;
      void queryClient.invalidateQueries({ queryKey: notesKey }).then(() => {
        const readsAfter = queryClient.getQueryState(notesKey)?.dataUpdateCount;
        // Only a read that answered after the save speaks for the server; then its value wins.
        if (readsAfter !== readsBefore) {
          setSaved((current) => (current === entry ? undefined : current));
        }
      });
      void queryClient.invalidateQueries({ queryKey: versionTreeKey(mockupId) });
    } catch (cause) {
      setRefusal(userMessage(asApiError(cause), t("versions.note.failed")));
    } finally {
      sending.current = false;
      setPending(false);
    }
  };

  if (draft !== null) {
    return (
      <div className={styles.editor}>
        <Input
          id={fieldId}
          value={draft}
          onChange={setDraft}
          label={t("versions.note.labelOf", { n: sequenceNumber })}
          placeholder={t("versions.note.placeholder")}
          maxLength={NOTE_MAX_LENGTH}
          readOnly={pending}
          invalid={refusal !== undefined}
          onEnter={() => {
            void save();
          }}
          onEscape={cancel}
        />
        <Button
          variant="primary"
          size="sm"
          loading={pending}
          onClick={() => {
            void save();
          }}
        >
          {t("versions.note.save")}
        </Button>
        <Button variant="ghost" size="sm" onClick={cancel}>
          {t("common.action.cancel")}
        </Button>
        {refusal === undefined ? null : (
          <p className={styles.error} role="alert">
            {refusal}
          </p>
        )}
      </div>
    );
  }

  if (shown === null) {
    if (!editable) return null;
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          setDraft("");
        }}
      >
        {t("versions.note.add")}
      </Button>
    );
  }

  return (
    <p className={styles.note}>
      <span>{shown}</span>
      {editable ? (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          label={t("versions.note.editOf", { n: sequenceNumber })}
          onClick={() => {
            setDraft(shown);
          }}
        >
          <Icon name="tag" size="sm" />
        </Button>
      ) : null}
    </p>
  );
}
